/**
 * Centralized Dependency Initialization
 *
 * This module provides a single point for dependency initialization
 * to avoid circular dependency issues between modules.
 *
 * Both columnConfig.tsx and renderFormField.tsx use this module
 * to initialize their dependencies on first use.
 *
 * Note: We use ES module imports (not require()) since this runs in the browser.
 * The imports are at the top level, but the setters are called lazily to ensure
 * the imported modules are fully initialized before we use them.
 */

import type { ColumnDef } from '@tanstack/react-table';
import type { TypeConfig, ResolvedField } from './types';
import type { ActionsConfig } from './columnModel';
import {
  setCreateStandardActionsColumn,
  setGetLookupApiForTagsDropdown,
} from './columnTypes/inline';
import { setGetLookupApi } from './columnTypes/dropdown';
import { setGetLookupApiForMultiselect } from './columnTypes/multiselect';
import {
  setResolveFieldConfig as setStatusResolveFieldConfig,
  setGetTypeConfig as setStatusGetTypeConfig,
} from './columnTypes/status';
import {
  setResolveFieldConfig as setBooleanResolveFieldConfig,
  setGetTypeConfig as setBooleanGetTypeConfig,
} from './columnTypes/boolean';

// Import directly from registry (no circular dependency)
import { getLookupApi } from './columnTypes/registry';

// ============================================================================
// Lazy Dependency Initialization
// ============================================================================

// Track if dependencies have been initialized
let columnConfigInitialized = false;
let formFieldInitialized = false;

// Store references to functions that will be set after module initialization
let resolveFieldConfigFn: ((col: { id: string; type?: string }) => ResolvedField) | null = null;
let getTypeConfigFn: ((type: string) => TypeConfig | undefined) | null = null;
let createStandardActionsColumnFn:
  | (<T extends { id: number | string }>(config: ActionsConfig<T>) => Partial<ColumnDef<T>>)
  | null = null;

/**
 * Set the resolveFieldConfig function (called from columnConfig.tsx)
 */
export const setResolveFieldConfig = (
  fn: (col: { id: string; type?: string }) => ResolvedField
): void => {
  resolveFieldConfigFn = fn;
};

/**
 * Set the getTypeConfig function (called from columnConfig.tsx)
 */
export const setGetTypeConfig = (fn: (type: string) => TypeConfig | undefined): void => {
  getTypeConfigFn = fn;
};

/**
 * Set the createStandardActionsColumn function (called from createColumns.tsx)
 */
export const setCreateStandardActionsColumnFn = <T extends { id: number | string }>(
  fn: (config: ActionsConfig<T>) => Partial<ColumnDef<T>>
): void => {
  createStandardActionsColumnFn = fn as <U extends { id: number | string }>(
    config: ActionsConfig<U>
  ) => Partial<ColumnDef<U>>;
};

/**
 * Initialize dependencies for column rendering (columnConfig.tsx)
 * Called on first use of renderColumnCell.
 *
 * Note: This must be called BEFORE any renderer is invoked, as renderers
 * depend on getLookupApi and resolveFieldConfig being set.
 */
export const initializeColumnConfigDependencies = (): void => {
  if (columnConfigInitialized) return;
  columnConfigInitialized = true;

  // Set up the dependency injection for dropdown renderer
  // getLookupApi is imported directly from registry (no circular dependency)
  setGetLookupApi(getLookupApi);

  // Set up the dependency injection for tags-dropdown renderer
  setGetLookupApiForTagsDropdown(getLookupApi);

  // Set up the dependency injection for multiselect editable renderer (e.g. categories -> GET /itemCategory)
  setGetLookupApiForMultiselect(getLookupApi);

  // Set up the dependency injection for status/boolean renderers
  // resolveFieldConfigFn is set by columnConfig.tsx during its initialization
  if (resolveFieldConfigFn) {
    setStatusResolveFieldConfig(resolveFieldConfigFn);
    setBooleanResolveFieldConfig(resolveFieldConfigFn);
  }

  // Set up the dependency injection for actions renderer
  // createStandardActionsColumnFn is set by createColumns.tsx during its initialization
  if (createStandardActionsColumnFn) {
    setCreateStandardActionsColumn(createStandardActionsColumnFn);
  }
};

/**
 * Initialize dependencies for form field rendering (renderFormField.tsx)
 * Called on first use of renderFormField.
 */
export const initializeFormFieldDependencies = (): void => {
  if (formFieldInitialized) return;
  formFieldInitialized = true;

  // Set up the dependency injection for dropdown renderer
  setGetLookupApi(getLookupApi);

  // Set up the dependency injection for multiselect form field (e.g. categories -> GET /itemCategory)
  setGetLookupApiForMultiselect(getLookupApi);

  // Set up the dependency injection for status/boolean form fields
  if (getTypeConfigFn) {
    setStatusGetTypeConfig(getTypeConfigFn);
    setBooleanGetTypeConfig(getTypeConfigFn);
  }
};

/**
 * Reset initialization state (for testing purposes only)
 * @internal
 */
export const resetInitializationForTesting = (): void => {
  columnConfigInitialized = false;
  formFieldInitialized = false;
};
